import { Link } from 'react-router-dom';
import { Shell } from '../components/Shell';

export function LandingPage() {
  return (
    <Shell>
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[1180px] mx-auto px-6 md:px-14 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <div>
            <span className="pill">
              <span className="pill-dot" />
              01 · EDPB + GDPR + ePrivacy
            </span>
            <h1
              className="font-medium m-0 mt-7"
              style={{
                fontSize: 'clamp(36px, 4.6vw, 64px)',
                lineHeight: 1.0,
                letterSpacing: '-0.035em',
                paddingRight: '0.15em',
              }}
            >
              A cookie banner and a <span className="grad-text" style={{ paddingRight: '0.08em' }}>fully functional CMP</span>.
            </h1>
            <p
              className="mt-7 mb-9 text-lg leading-[1.55]"
              style={{ color: 'rgba(13,11,31,0.72)', maxWidth: 560 }}
            >
              An easy to customize and drop CMP, built and reviewed by compliance experts.
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <a href="https://scanner.wedpo.com/?source=cmp" className="btn btn-bright">
                <span>Scan my URL for compliance issues</span>
                <span className="slash">/</span>
              </a>
              <Link to="/signup" className="btn">
                <span>Start free</span>
                <span className="slash">/</span>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-7 max-w-[640px]">
              <div className="pt-4" style={{ borderTop: '1px solid var(--ink)' }}>
                <div className="meta mb-3">Lawyer-reviewed</div>
                <p className="text-[13px] leading-[1.55]" style={{ color: 'rgba(13,11,31,0.7)' }}>
                  Defaults built on EDPB Cookie Banner Taskforce guidance, with article-level citations.
                </p>
              </div>
              <div className="pt-4" style={{ borderTop: '1px solid var(--ink)' }}>
                <div className="meta mb-3">Drop-in install</div>
                <p className="text-[13px] leading-[1.55]" style={{ color: 'rgba(13,11,31,0.7)' }}>
                  Two HTML lines. Works anywhere a script tag lives, from WordPress to Next.js.
                </p>
              </div>
              <div className="pt-4" style={{ borderTop: '1px solid var(--ink)' }}>
                <div className="meta mb-3">Built by DPOs</div>
                <p className="text-[13px] leading-[1.55]" style={{ color: 'rgba(13,11,31,0.7)' }}>
                  Operated by Entero Strategies. The product passes the same audits it runs on others.
                </p>
              </div>
            </div>
          </div>

          <BrowserVisual />
        </div>
      </section>
    </Shell>
  );
}

function StepCircle({ status, index }: { status: 'done' | 'current' | 'todo'; index: number }) {
  const done = status === 'done';
  const current = status === 'current';
  return (
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: done ? 'var(--live)' : 'white',
        border: done
          ? '2px solid var(--live)'
          : current
          ? '2px solid var(--grad-1)'
          : '2px solid rgba(13,11,31,0.18)',
        color: done ? 'white' : current ? 'var(--grad-1)' : 'rgba(13,11,31,0.45)',
        fontSize: 10,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {done ? '✓' : index}
    </span>
  );
}

function BrowserVisual() {
  return (
    <div className="relative" style={{ minHeight: 480 }}>
      {/* Soft gradient backdrop blobs */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-10%',
            right: '-10%',
            width: 320,
            height: 320,
            background: 'radial-gradient(circle, rgba(62,199,217,0.35) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-15%',
            left: '-5%',
            width: 360,
            height: 360,
            background: 'radial-gradient(circle, rgba(123,63,228,0.28) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
        />
      </div>

      {/* Getting started progress card - top right */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 320,
          background: 'white',
          border: '1px solid rgba(13,11,31,0.1)',
          borderRadius: 10,
          padding: '14px 18px 16px',
          boxShadow: '0 22px 50px -25px rgba(13,11,31,0.35)',
          transform: 'rotate(2deg)',
          zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#0D0B1F' }}>
            Getting started <span style={{ color: 'var(--muted-ink)', fontWeight: 400 }}>· 2 of 4 done</span>
          </span>
          <span style={{ fontSize: 9, color: 'var(--muted-ink)', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Next: Install
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {[
            { label: 'Configure', status: 'done' },
            { label: 'Preview', status: 'done' },
            { label: 'Publish', status: 'current' },
            { label: 'Install', status: 'todo' },
          ].map((step, i) => (
            <div key={step.label} style={{ display: 'flex', alignItems: 'center', flex: i === 3 ? '0 0 auto' : 1 }}>
              <StepCircle status={step.status as 'done' | 'current' | 'todo'} index={i + 1} />
              {i < 3 && (
                <span
                  style={{
                    flex: 1,
                    height: 2,
                    background:
                      step.status === 'done' ? 'var(--live)' : 'rgba(13,11,31,0.1)',
                    margin: '0 4px',
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 9, color: 'var(--muted-ink)', fontWeight: 500 }}>
          <span>Configure</span>
          <span>Preview</span>
          <span style={{ color: 'var(--grad-1)' }}>Publish</span>
          <span>Install</span>
        </div>

        <div style={{ marginTop: 12, padding: '8px 10px', background: 'rgba(123,63,228,0.06)', borderRadius: 4, fontSize: 10.5, lineHeight: 1.5, color: 'rgba(13,11,31,0.78)' }}>
          <strong style={{ color: 'var(--grad-1)' }}>Next:</strong> click <strong>Publish</strong> in the top right. Edge cache picks up in about 5 minutes.
        </div>

        <button
          style={{
            marginTop: 10,
            padding: '6px 12px',
            background: 'var(--grad-1)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Open Publish
        </button>
      </div>

      {/* Preferences modal - back layer */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 10,
          width: 240,
          background: 'white',
          border: '1px solid rgba(13,11,31,0.1)',
          borderRadius: 8,
          padding: '14px 16px',
          boxShadow: '0 20px 50px -25px rgba(13,11,31,0.35)',
          transform: 'rotate(-4deg)',
          zIndex: 1,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: '#0D0B1F' }}>
          Manage your preferences
        </div>
        {[
          { name: 'Essential', desc: 'Always on', on: true, lock: true },
          { name: 'Functional', desc: 'Preferences', on: true, lock: false },
          { name: 'Analytics', desc: 'Usage stats', on: false, lock: false },
          { name: 'Marketing', desc: 'Ads & tracking', on: false, lock: false },
        ].map((cat, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 0',
              borderBottom: i < 3 ? '1px solid rgba(13,11,31,0.06)' : 'none',
            }}
          >
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 500, color: '#0D0B1F' }}>{cat.name}</div>
              <div style={{ fontSize: 9, color: 'var(--muted-ink)' }}>{cat.desc}</div>
            </div>
            <span
              style={{
                width: 22,
                height: 12,
                borderRadius: 99,
                background: cat.on ? 'var(--ink)' : 'var(--paper-2)',
                position: 'relative',
                opacity: cat.lock ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  width: 9,
                  height: 9,
                  top: 1.5,
                  left: cat.on ? 11 : 1.5,
                  background: 'white',
                  borderRadius: '50%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}
              />
            </span>
          </div>
        ))}
      </div>

      {/* Main banner card - front layer */}
      <div
        style={{
          position: 'relative',
          background: 'white',
          borderRadius: 10,
          padding: '22px 26px 18px',
          maxWidth: 540,
          marginLeft: 'auto',
          marginTop: 130,
          border: '1px solid rgba(13,11,31,0.12)',
          boxShadow: '0 40px 80px -30px rgba(13,11,31,0.45), 0 8px 20px -10px rgba(13,11,31,0.2)',
          zIndex: 2,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'rgba(13,11,31,0.75)', marginBottom: 8 }}>
          <span style={{ fontSize: 16 }}>🍪</span>
          <span>WeDPO · Cookie Notice</span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6, lineHeight: 1.25, color: '#0D0B1F', letterSpacing: '-0.01em' }}>
          We use essential cookies, and ask before anything else
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'rgba(13,11,31,0.7)', margin: '0 0 14px' }}>
          yourdomain.com uses a small set of technologies that are strictly necessary to make the site work. Non-essential cookies stay off until you opt in. See our{' '}
          <a href="#" style={{ color: '#0D0B1F', textDecoration: 'underline' }}>Cookie Policy</a>.
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <button style={{ padding: '8px 14px', fontSize: 12, borderRadius: 4, border: '1px solid #0D0B1F', background: '#0D0B1F', color: 'white', fontWeight: 500, cursor: 'pointer' }}>Accept all</button>
          <button style={{ padding: '8px 14px', fontSize: 12, borderRadius: 4, border: '1px solid rgba(13,11,31,0.5)', background: 'white', color: '#0D0B1F', fontWeight: 500, cursor: 'pointer' }}>Reject all</button>
          <button style={{ padding: '8px 14px', fontSize: 12, borderRadius: 4, border: '1px solid transparent', background: 'transparent', color: 'rgba(13,11,31,0.7)', fontWeight: 500, cursor: 'pointer' }}>Manage preferences</button>
        </div>
        <div style={{ paddingTop: 10, borderTop: '1px solid rgba(13,11,31,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10, fontSize: 10.5, color: 'rgba(13,11,31,0.65)' }}>
            <a href="#" style={{ color: 'inherit', textDecoration: 'underline' }}>Cookie Policy</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Notice</a>
            <a href="#" style={{ color: 'inherit', textDecoration: 'underline' }}>License Agreement</a>
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(13,11,31,0.55)', display: 'flex', alignItems: 'center', gap: 3 }}>
            Powered by <span className="grad-text" style={{ fontWeight: 700, letterSpacing: '-0.02em' }}>we/dpo</span> CMP
            <span style={{ color: 'rgba(13,11,31,0.45)', marginLeft: 2 }}>· v1.0</span>
          </div>
        </div>
      </div>

      {/* Badge - bottom right of composition */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          right: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          background: 'white',
          border: '1px solid rgba(13,11,31,0.1)',
          borderRadius: 99,
          boxShadow: '0 10px 30px -15px rgba(13,11,31,0.3)',
          zIndex: 3,
          fontSize: 11,
          fontFamily: 'IBM Plex Mono, monospace',
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          color: 'var(--ink)',
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--live)' }} />
        EDPB · Cookie Banner Taskforce 2023
      </div>
    </div>
  );
}
